import React, { useMemo, useState } from "react";
import {
    FlexContainer,
    TextLabel,
    CardHeadingLabel,
    Text,
    StandardModal,
    StandardModalActionButton,
    StandardModalFormGrid,
    StandardModalSection,
    PortalPaymentOptionGrid,
    PortalPaymentSummaryAmount,
    PortalPaymentSummaryCard,
    PortalPaymentSummaryLabel,
    PortalPaymentSummaryRow
} from "src/components";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { formatProratedPeriod } from "./pricingUtils";
import EmailProPayment from "./EmailProPayment";
import { PaymentSelectionButton, PaymentSelectionIcon } from "src/scenes/Subscription/forms/Review";

const METHODS = {
    EXISTING: "EXISTING",
    NEW: "NEW",
};

export default function EmailUpgradeModal({ user, plan, onClose, statistics, onBack, onPurchaseSuccess, proratePricing }) {

    const [selectedMethod, setSelectedMethod] = useState(METHODS.EXISTING);
    const [isProcessing, setIsProcessing] = useState(false);

    const prorated = useMemo(() => {
        if (!statistics?.proratedPeriod) return null;
        return formatProratedPeriod(statistics.proratedPeriod);
    }, [statistics]);

    const currentProrated = useMemo(() => {
        if (!proratePricing || !plan) return null;
        return proratePricing.find(p => p.addonId === plan.uuid) || null;
    }, [proratePricing, plan]);

    return (
        <StandardModal
            open={true}
            onClose={onClose}
            title="Complete Upgrade to Email Pro"
            description="You’re upgrading to Email Pro part-way through your current Me Business subscription."
            widthMode="wide"
        >
            <StandardModalFormGrid columns="repeat(2, minmax(0, 1fr))" gap="1rem">
                <StandardModalSection padding="1.5rem" gap="1rem">
                    <CardHeadingLabel >Order Summary</CardHeadingLabel>
                    <FlexContainer gap="14px" marginBottom="20px" flex="1" direction="column">
                        <FlexContainer direction>
                            <TextLabel weight="600" color="#8E4EB4">{plan?.title}</TextLabel>
                            <TextLabel color="#8E4EB4">{`AUD $${plan?.price} / year`}</TextLabel>
                        </FlexContainer>
                        <FlexContainer>
                            <TextLabel weight="600" color="#8E4EB4">Main Subscription Status</TextLabel>
                            <Text color="#8E4EB4" align="left" size="14px" font="Manrope" style={{ marginTop: "3px" }}>
                                { "Me Business"} — {prorated?.paymentMode} Plan<br/>
                                {prorated?.display}, Renews On: {prorated?.endsAt}

                            </Text>
                        </FlexContainer>
                    </FlexContainer>

                    {/* Billing Breakdown */}
                    <FlexContainer gap="10px" flex="1" direction="column" style={{ marginTop: "-12px" }}>
                        <TextLabel weight="600" color="#000000">Billing Breakdown</TextLabel>

                        {/* Email Pro Plan */}
                        <FlexContainer justifyContent="space-between" direction="row">
                            <TextLabel>Email Pro Plan:</TextLabel>
                            <TextLabel weight="500">{plan?.title}</TextLabel>
                        </FlexContainer>

                        {/* Prorated Period */}
                        <FlexContainer justifyContent="space-between" direction="row">
                            <TextLabel>Prorated Period:</TextLabel>
                            <TextLabel weight="500">
                                {prorated?.display}
                            </TextLabel>
                        </FlexContainer>

                        {/* Price (Prorated) */}
                        <FlexContainer justifyContent="space-between" direction="row" marginBottom="20px">
                            <TextLabel>Price (Prorated):</TextLabel>
                            <TextLabel weight="500">AUD ${currentProrated?.subtotal?.toFixed(2)}</TextLabel>
                        </FlexContainer>
                    </FlexContainer>

                    {/* Renewal Notice */}
                    <FlexContainer gap="10px" direction="column" marginBottom="20px">
                        <TextLabel weight="600" color="#8E4EB4">Renewal Notice</TextLabel>
                        <Text color="#8E4EB4" size="14px" font="Manrope" align="left" style={{ marginTop: "-5px" }}>
                                On your next Me Business renewal, Email Pro will renew at the full yearly price and be billed together with your main subscription.
                        </Text>
                    </FlexContainer>

                    {/* Billing Calculation Card */}
                    <PortalPaymentSummaryCard>
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>Subtotal</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount>
                                AUD ${currentProrated?.subtotal?.toFixed(2)}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>Processing Fee</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                AUD ${currentProrated?.processingFee?.toFixed(2)}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>GST</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                AUD ${currentProrated?.gst?.toFixed(2)}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>Total</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount size="1.25rem">
                                AUD ${currentProrated?.total?.toFixed(2)}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                    </PortalPaymentSummaryCard>
                    <FlexContainer direction="row" mDirection="row" tDirection="row" marginTop="20px" justifyContent="flex-start" gap="20px">
                        <StandardModalActionButton onClick={onBack}>Back</StandardModalActionButton>
                    </FlexContainer>
                </StandardModalSection>

                {/* RIGHT COLUMN: PAYMENT METHOD */}
                <StandardModalSection padding="1.5rem" gap="1rem">
                    <CardHeadingLabel>Payment Method</CardHeadingLabel>

                    {/* BUTTONS TO TOGGLE METHOD */}
                    {!isProcessing && (
                        <PortalPaymentOptionGrid>
                            <PaymentSelectionButton
                                $active={selectedMethod === METHODS.EXISTING}
                                onClick={() => setSelectedMethod(METHODS.EXISTING)}
                            >
                                <PaymentSelectionIcon $active={selectedMethod === METHODS.EXISTING} />
                                    Use an existing card
                            </PaymentSelectionButton>
                            <PaymentSelectionButton
                                $active={selectedMethod === METHODS.NEW}
                                onClick={() => setSelectedMethod(METHODS.NEW)}
                            >
                                <PaymentSelectionIcon $active={selectedMethod === METHODS.NEW} />
                                    Add new card
                            </PaymentSelectionButton>
                        </PortalPaymentOptionGrid>
                    )}

                    {/* CONDITIONAL DISPLAY */}
                    {selectedMethod === METHODS.EXISTING ? (
                        <EmailProPayment
                            plan={plan}
                            onClose={onClose}
                            onOpenUpgradeModal={() => setSelectedMethod(METHODS.NEW)}
                            onPurchaseSuccess={onPurchaseSuccess}
                            onProcessingChange={setIsProcessing}
                        />
                    ) : (
                        <AirwallexForm
                            productType={AirwallexProduct.EMAIL_ADDON}
                            token={user?.token}
                            companyName={user?.companyName}
                            email={user?.email}
                            addonUuid={plan?.uuid}
                            nextClick={onPurchaseSuccess}
                            backToPrevious={onClose}
                            onPaymentProcessingChange={setIsProcessing}
                        />
                    )}
                </StandardModalSection>
            </StandardModalFormGrid>
        </StandardModal>
    );
}
